import { createRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { platformLayoutRoute } from './platformLayout.js';
import {
  listTenants,
  type ListTenantsParams,
  type SubscriptionStatus,
  type TenantListRow,
} from '../../api/platform.js';
import { PlatformApiError } from '../../lib/platform-auth.js';
import { StatusBadge } from '../../components/StatusBadge.js';

/**
 * /platform/tenants — cross-tenant list. Mirrors the tenant employees.tsx list
 * pattern (TanStack Query + plain <table> + Loading/Empty/Error states), with
 * debounced search, a status filter, a sort dropdown, and cursor pagination
 * (Next/Prev via a small cursor stack).
 *
 * Read-only: rows link to detail; there are no mutation actions.
 */
export const platformTenantsRoute = createRoute({
  getParentRoute: () => platformLayoutRoute,
  path: '/platform/tenants',
  component: PlatformTenantsPage,
});

type StatusFilter = 'all' | SubscriptionStatus;

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'read_only', label: 'Read-Only' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];
type SortChoice = 'name:asc' | 'name:desc' | 'createdAt:asc' | 'createdAt:desc';

const PAGE_SIZE = 20;

/** Debounce a value by `delayMs` (search-as-you-type without hammering the API). */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function PlatformTenantsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortChoice>('name:asc');
  // Cursor pagination: `cursor` is the current page; `history` is the stack of
  // prior page cursors so Previous can walk back.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<(string | undefined)[]>([]);

  const search = useDebounced(searchInput, 300);

  useEffect(() => {
    document.title = 'Platform — Tenants';
  }, []);

  // Reset pagination whenever the filters/sort change.
  useEffect(() => {
    setCursor(undefined);
    setHistory([]);
  }, [search, status, sort]);

  const [sortBy, sortOrder] = sort.split(':') as [ListTenantsParams['sortBy'], ListTenantsParams['sortOrder']];

  const params: ListTenantsParams = {
    limit: PAGE_SIZE,
    sortBy,
    sortOrder,
    ...(cursor ? { cursor } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(search ? { search } : {}),
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['platform', 'tenants', params],
    queryFn: () => listTenants(params),
  });

  function goNext(): void {
    if (!data?.nextCursor) return;
    setHistory((h) => [...h, cursor]);
    setCursor(data.nextCursor);
  }

  function goPrev(): void {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setCursor(prev);
      return h.slice(0, -1);
    });
  }

  return (
    <section className="p-8 space-y-6">
      <header>
        <h2 className="text-2xl font-semibold text-gray-900">Tenants</h2>
        <p className="text-sm text-gray-500">All organisations on the platform. Read-only.</p>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          aria-label="Search tenants"
          placeholder="Search by name or country…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-deep focus:outline-none min-w-[16rem]"
        />
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-deep focus:outline-none"
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Sort by"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortChoice)}
          className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-deep focus:outline-none"
        >
          <option value="name:asc">Name (A–Z)</option>
          <option value="name:desc">Name (Z–A)</option>
          <option value="createdAt:desc">Newest first</option>
          <option value="createdAt:asc">Oldest first</option>
        </select>
      </div>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
      {data && data.tenants.length === 0 ? <EmptyState /> : null}

      {data && data.tenants.length > 0 ? (
        <>
          <TenantTable
            tenants={data.tenants}
            onRowClick={(id) => void navigate({ to: '/platform/tenants/$id', params: { id } })}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={history.length === 0}
              className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!data.nextCursor}
              className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

function TenantTable({
  tenants,
  onRowClick,
}: {
  tenants: TenantListRow[];
  onRowClick: (id: string) => void;
}): JSX.Element {
  return (
    <div className="rounded border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium">Country</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Employees</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr
              key={t.id}
              onClick={() => onRowClick(t.id)}
              className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-4 py-3 font-medium text-indigo-deep">{t.name}</td>
              <td className="px-4 py-3 text-gray-600">{t.countryCode}</td>
              <td className="px-4 py-3">
                {t.status ? <StatusBadge status={t.status} size="sm" /> : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-4 py-3 text-gray-600">{t.employeeCount}</td>
              <td className="px-4 py-3 text-gray-600">{formatDate(t.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-200 p-8 text-center text-gray-500">
      Loading tenants…
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-dashed border-gray-300 p-10 text-center text-gray-500">
      No tenants found matching your criteria.
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }): JSX.Element {
  const message =
    error instanceof PlatformApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <div role="alert" className="rounded border border-red-200 bg-red-50 p-6 space-y-3">
      <h3 className="font-medium text-red-700">Could not load tenants</h3>
      <p className="text-sm text-gray-600">{message}</p>
      <button type="button" onClick={onRetry} className="rounded border border-red-300 px-3 py-1 text-xs hover:bg-red-100">
        Retry
      </button>
    </div>
  );
}
